import React, { useState, useEffect } from "react";
import { Box, Typography, Stack, useTheme, Paper, Tooltip } from "@mui/material";

const DAYS_LABELS = ["DOM", "LUN", "MAR", "MIE", "JUE", "VIE", "SAB"];

const formatTimeHM = (time) => {
  if (!time) return "";
  return String(time).slice(0, 5);
};

export default function VistaDia({
  currentDate,
  events,
  searchTerm,
  onEventClick,
  isToday,
}) {
  const theme = useTheme();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const d = currentDate.getDate();
  const m = currentDate.getMonth();
  const y = currentDate.getFullYear();
  const today = isToday(d, m, y);

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const hourHeight = 42;
  const dayHeight = hours.length * hourHeight;
  const hourColumnWidth = 44;
  const hourLineOffset = 0;

  const getEventsForDay = () => {
    const cellDateStr = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

    return events.filter((event) => {
      if (searchTerm && !event.titulo.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (!event.fecha) return false;
      if (typeof event.fecha === "string") return event.fecha.startsWith(cellDateStr);
      const eventDate = new Date(event.fecha);
      return eventDate.getDate() === d && eventDate.getMonth() === m && eventDate.getFullYear() === y;
    });
  };

  const getEventPosition = (evt) => {
    if (!evt.horaInicio) return { top: 0, height: hourHeight, continuesAfterDay: false };

    const [startH, startM] = evt.horaInicio.split(":").map(Number);
    const top = (startH * hourHeight) + (startM * hourHeight / 60);

    let height = hourHeight;
    if (evt.horaFin) {
      const [endH, endM] = evt.horaFin.split(":").map(Number);
      const normalizedEndH = endH < startH || (endH === startH && endM <= startM) ? endH + 24 : endH;
      height = ((normalizedEndH - startH) * hourHeight) + ((endM - startM) * hourHeight / 60);
    }

    const safeTop = Math.max(0, top);
    const unclampedHeight = Math.max(height, 25);
    const maxHeight = Math.max(0, dayHeight - safeTop);
    const clampedHeight = Math.min(unclampedHeight, maxHeight);

    return {
      top: safeTop,
      height: clampedHeight,
      continuesAfterDay: safeTop + unclampedHeight > dayHeight,
    };
  };

  const layoutTimedEvents = (timedEvents) => {
    const positionedEvents = timedEvents
      .map((evt) => {
        const position = getEventPosition(evt);
        return {
          evt,
          ...position,
          end: position.top + position.height,
          column: 0,
          columnCount: 1,
        };
      })
      .sort((a, b) => a.top - b.top || a.end - b.end);

    const groups = [];
    positionedEvents.forEach((item) => {
      const currentGroup = groups[groups.length - 1];
      if (!currentGroup || item.top >= currentGroup.end) {
        groups.push({ end: item.end, items: [item] });
        return;
      }

      currentGroup.items.push(item);
      currentGroup.end = Math.max(currentGroup.end, item.end);
    });

    groups.forEach((group) => {
      const columnEnds = [];
      group.items.forEach((item) => {
        const column = columnEnds.findIndex((end) => end <= item.top);
        const targetColumn = column === -1 ? columnEnds.length : column;
        item.column = targetColumn;
        columnEnds[targetColumn] = item.end;
      });

      group.items.forEach((item) => {
        item.columnCount = columnEnds.length;
      });
    });

    return positionedEvents;
  };

  const getCurrentTimePosition = () => {
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    return (currentHour * hourHeight) + (currentMinute * hourHeight / 60);
  };

  const dayEvents = getEventsForDay();
  const allDayEvents = dayEvents.filter((evt) => !evt.horaInicio);
  const timedEvents = layoutTimedEvents(dayEvents.filter((evt) => !!evt.horaInicio));
  const timeLineTop = today ? getCurrentTimePosition() : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", bgcolor: "background.paper", borderRadius: 1, minHeight: 0 }}>
      <Box sx={{ display: "flex", borderBottom: `1px solid ${theme.palette.divider}`, minHeight: 76, bgcolor: "#fafafa" }}>
        <Box
          sx={{
            width: hourColumnWidth,
            flexShrink: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: today ? "primary.main" : "transparent",
              color: today ? "primary.contrastText" : "text.primary",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>
              {DAYS_LABELS[currentDate.getDay()]}
            </Box>
            <Box component="span" sx={{ fontSize: 18 }}>
              {d}
            </Box>
          </Typography>
        </Box>

        <Box sx={{ width: 72, flexShrink: 0, px: 0.5, py: 0.75, display: "flex", alignItems: "center", justifyContent: "center", borderRight: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: "bold", textAlign: "center", fontSize: "0.62rem", lineHeight: 1.05, textTransform: "uppercase" }}>
            Todo<br />el día
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, p: 1, bgcolor: allDayEvents.length > 0 ? "#fffde7" : "transparent" }}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignContent: "flex-start" }}>
            {allDayEvents.map((evt, index) => {
              const eventColor = evt.color || theme.palette.primary.main;
              return (
                <Tooltip key={`${evt.id || evt.titulo || "all-day"}-${index}`} title={evt.titulo || ""} arrow enterDelay={350}>
                <Box
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onEventClick) onEventClick(evt);
                  }}
                  sx={{
                    bgcolor: eventColor,
                    color: "common.white",
                    px: 1.5,
                    py: 0.4,
                    borderRadius: "4px",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    width: 180,
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: 1.15,
                    cursor: "pointer",
                    "&:hover": { filter: "brightness(0.95)" },
                  }}
                >
                  {evt.titulo}
                </Box>
                </Tooltip>
              );
            })}
          </Stack>
        </Box>
      </Box>

      <Box sx={{ display: "flex", height: { xs: 320, lg: 380 }, overflowY: "auto", position: "relative" }}>
        <Box sx={{ width: hourColumnWidth, flexShrink: 0, borderRight: `1px solid ${theme.palette.divider}`, position: "relative", bgcolor: "background.paper", zIndex: 2 }}>
          {hours.map((hour) => (
            <Box key={hour} sx={{ height: hourHeight, position: "relative" }}>
              <Typography variant="caption" sx={{ position: "absolute", top: 2, right: 4, color: "text.secondary", fontWeight: "bold", bgcolor: "background.paper", px: 0.25, fontSize: "0.68rem" }}>
                {String(hour).padStart(2, "0")}:00
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1, position: "relative", height: `${dayHeight}px` }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: `${dayHeight}px`,
              zIndex: 0,
              pointerEvents: "none",
              backgroundImage: `linear-gradient(${theme.palette.divider} 1px, transparent 1px)`,
              backgroundSize: `100% ${hourHeight}px`,
              backgroundPositionY: `${hourLineOffset}px`,
            }}
          />

          {timeLineTop !== null && (
            <Box
              sx={{
                position: "absolute",
                top: timeLineTop,
                left: 0,
                right: 0,
                height: "2px",
                bgcolor: "red",
                zIndex: 10,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: -4,
                  top: -3,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "red",
                },
              }}
            />
          )}

          {timedEvents.map(({ evt, top, height, column, columnCount, continuesAfterDay }, index) => {
            const eventColor = evt.color || theme.palette.primary.main;
            const isOverlapping = columnCount > 1;
            const visibleHeight = Math.max(height - 2, 1);

            return (
              <Tooltip key={`${evt.id || evt.titulo || "timed"}-${index}`} title={evt.titulo || ""} arrow enterDelay={350}>
              <Paper
                tabIndex={0}
                elevation={3}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onEventClick) onEventClick(evt);
                }}
                sx={{
                  position: "absolute",
                  top,
                  height: visibleHeight,
                  left: isOverlapping ? `calc(${(column * 100) / columnCount}% + 4px)` : 4,
                  right: isOverlapping ? "auto" : 4,
                  width: isOverlapping ? `calc(${100 / columnCount}% - 8px)` : "auto",
                  bgcolor: `${eventColor}1A`,
                  color: eventColor,
                  borderLeft: `4px solid ${eventColor}`,
                  p: 0.6,
                  borderRadius: "4px",
                  borderBottomLeftRadius: continuesAfterDay ? 0 : "4px",
                  borderBottomRightRadius: continuesAfterDay ? 0 : "4px",
                  cursor: "pointer",
                  minWidth: 0,
                  boxSizing: "border-box",
                  overflow: "hidden",
                  transition: "all 0.2s",
                  "&:hover": {
                    filter: "brightness(0.95)",
                    zIndex: 30,
                    boxShadow: 6,
                  },
                }}
              >
                <Typography variant="caption" display="block" sx={{ fontWeight: "bold", lineHeight: 1.2 }}>
                  {formatTimeHM(evt.horaInicio)} {evt.horaFin ? `- ${formatTimeHM(evt.horaFin)}` : ""}
                </Typography>
                <Typography variant="body2" noWrap sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2, fontWeight: 500 }}>
                  {evt.titulo}
                </Typography>
              </Paper>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
